import { WorkListTools } from '@/app/work-tabs/work-list-tools';
import { newWorkPath, useWorkTab } from '@/app/work-tabs/work-tab-context';
import {
  PERMISSIONS,
  listViewDefaultColumns,
  customFieldFiltersQuerySchema,
  SHELF_LIFE_CRITICAL_DAYS,
  type CustomFieldFilterValues,
  type ProductDetail,
} from '@erp/shared';
import { Download, FolderTree, Plus, Search, Tag } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ListViewToolbar, useListView } from '@/features/list-views';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/features/auth';
import {
  CustomFieldListFilters,
  activeCustomFieldFilterCount,
  useCustomFieldDefinitions,
} from '@/features/custom-fields';
import {
  useProductExport,
  useProductList,
  type ProductListParams,
} from '@/features/products/api/use-products';
import { BulkPriceDialog } from '@/features/products/components/bulk-price-dialog';
import { ProductFilters } from '@/features/products/components/product-filters';
import { ProductFormSheet } from '@/features/products/components/product-form-sheet';
import { ProductCategoryManager } from '@/features/products/components/product-category-manager';
import { ProductTable } from '@/features/products/components/product-table';
import {
  EMPTY_PRODUCT_FILTERS,
  hasActiveProductFilters,
  type ProductFiltersValue,
} from '@/features/products/lib/product-filters-state';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

export function ProductsRoute(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const workTab = useWorkTab();
  const { can } = usePermissions();
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  const definitions = useCustomFieldDefinitions('PRODUCT');
  const views = useListView('products', {
    filters: {
      ...Object.fromEntries(
        [...searchParams].filter(
          ([key]) => key in EMPTY_PRODUCT_FILTERS || key === 'customFieldFilters',
        ),
      ),
      ...(searchParams.get('expiring') === '1'
        ? { expiringWithinDays: String(SHELF_LIFE_CRITICAL_DAYS) }
        : {}),
      ...(searchParams.get('stock') === 'LOW' ? { belowMinStock: 'true' } : {}),
    },
    sort:
      searchParams.get('sort') === 'createdAt'
        ? 'createdAt:desc'
        : (searchParams.get('sort') ?? 'articleNumber:asc'),
    columns: [
      ...listViewDefaultColumns('products'),
      ...(definitions.data ?? [])
        .filter((d) => d.showInList && d.archivedAt === null)
        .map((d) => ({ id: `cf-${d.id}`, visible: true })),
    ],
    fieldRefs: {},
    ...(searchParams.get('q') ? { search: searchParams.get('q') ?? '' } : {}),
  });
  const search = views.state.search ?? '';
  const setSearch = (value: string): void => views.commit({ ...views.state, search: value }, true);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const { page, setPage } = views;
  const sort = views.state.sort;
  const setSort = (value: string): void => views.commit({ ...views.state, sort: value });
  const f = views.state.filters;
  const filters: ProductFiltersValue = {
    status: (f['status'] ?? '').split(',').filter(Boolean),
    type: (f['type'] ?? '').split(',').filter(Boolean),
    productGroup: (f['productGroup'] ?? '').split(',').filter(Boolean),
    isBatchManaged: f['isBatchManaged'] === undefined ? undefined : f['isBatchManaged'] === 'true',
    belowMinStock: f['belowMinStock'] === 'true',
    expiringWithinDays:
      f['expiringWithinDays'] === undefined ? undefined : Number(f['expiringWithinDays']),
    activeOnly: f['activeOnly'] === 'true',
  };
  const setFilters = (next: ProductFiltersValue): void => {
    const value = { ...views.state.filters };
    for (const [key, entry] of Object.entries(next)) {
      if (entry === undefined || (Array.isArray(entry) && entry.length === 0)) delete value[key];
      else value[key] = Array.isArray(entry) ? entry.join(',') : String(entry);
    }
    views.commit({ ...views.state, filters: value });
  };
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectionScope = JSON.stringify([
    views.state.filters,
    views.state.sort,
    views.state.search,
  ]);
  useEffect(() => {
    setSelectedIds([]);
  }, [selectionScope]);
  const [formOpen, setFormOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const customFieldFilters =
    customFieldFiltersQuerySchema.safeParse(views.state.filters['customFieldFilters']).data ?? {};
  const setCustomFieldFilters = (value: CustomFieldFilterValues): void =>
    views.setFilter('customFieldFilters', JSON.stringify(value));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const params: ProductListParams = {
    page,
    pageSize: PAGE_SIZE,
    sort,
    q: debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim(),
    tags: (views.state.filters['tags'] ?? '').split(',').filter(Boolean),
    status: filters.status,
    type: filters.type,
    productGroup: filters.productGroup,
    isBatchManaged: filters.isBatchManaged,
    belowMinStock: filters.belowMinStock,
    expiringWithinDays: filters.expiringWithinDays,
    activeOnly: filters.activeOnly,
    customFieldFilters,
  };

  const list = useProductList(params, views.ready && debouncedSearch === search);
  const exportProducts = useProductExport();

  const canWrite = can(PERMISSIONS.PRODUCT_WRITE);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!workTab.active) return;
      const target = event.target as HTMLElement | null;
      const isEditable =
        target !== null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key === '/' && !isEditable) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (event.key === 'n' && !isEditable && canWrite) {
        event.preventDefault();
        if (workTab.managed) navigate(newWorkPath('/products/new'));
        else setFormOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canWrite, workTab.active, workTab.managed, navigate]);

  const onExport = (format: 'csv' | 'xlsx'): void => {
    exportProducts.mutate(
      { params, format },
      {
        onSuccess: () => toast.success(t('products.export.success')),
        onError: (error) => toast.error(t('products.export.error'), { description: error.message }),
      },
    );
  };

  const onProductSaved = (product: ProductDetail): void => {
    navigate(`/products/${product.id}`);
  };

  const meta = views.ready ? list.data?.meta : undefined;
  const facets = list.data?.facets;
  const hasFilters =
    debouncedSearch.trim() !== '' ||
    hasActiveProductFilters(filters) ||
    activeCustomFieldFilterCount(customFieldFilters) > 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('products.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('products.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCategoriesOpen(true)}>
            <FolderTree className="h-4 w-4" aria-hidden="true" />
            {t('products.categories.action')}
          </Button>
          {canWrite ? (
            <Button
              variant="outline"
              size="sm"
              disabled={selectedIds.length === 0}
              onClick={() => setBulkOpen(true)}
            >
              <Tag className="h-4 w-4" aria-hidden="true" />
              {t('products.actions.bulkPrice', { count: selectedIds.length })}
            </Button>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exportProducts.isPending || !views.ready}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {t('products.actions.export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => onExport('csv')}>
                {t('products.export.csv')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onExport('xlsx')}>
                {t('products.export.xlsx')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {canWrite ? (
            <Button
              size="sm"
              onClick={() =>
                workTab.managed ? navigate(newWorkPath('/products/new')) : setFormOpen(true)
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('products.actions.create')}
            </Button>
          ) : null}
        </div>
      </header>

      <WorkListTools>
        <ListViewToolbar
          controller={views}
          columnLabel={(id) =>
            id === 'select'
              ? t('products.selection.toggleAll')
              : (definitions.data?.find((d) => `cf-${d.id}` === id || d.key === id)?.label ??
                t(`products.columns.${id}`, {
                  defaultValue: t(`products.filters.${id}`, { defaultValue: id }),
                }))
          }
        />
        <div className="flex flex-col gap-2">
          <div className="relative min-w-64 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={searchRef}
              className="pl-9"
              placeholder={t('products.searchPlaceholder')}
              aria-label={t('products.searchLabel')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <ProductFilters
            value={filters}
            onChange={(next) => {
              setFilters(next);
            }}
            availableGroups={facets?.productGroups ?? []}
            belowMinStockCount={facets?.belowMinStock ?? 0}
            expiringCount={facets?.expiringSoon ?? 0}
          />
          <CustomFieldListFilters
            definitions={definitions.data ?? []}
            value={customFieldFilters}
            onChange={(next) => {
              setCustomFieldFilters(next);
            }}
          />
        </div>
      </WorkListTools>
      {views.error || views.issues.length > 0 ? null : (
        <ProductTable
          savedColumns={views.state.columns}
          onSavedColumnsChange={(columns) => views.commit({ ...views.state, columns })}
          rows={views.ready && !list.isPlaceholderData ? (list.data?.data ?? []) : []}
          definitions={definitions.data ?? []}
          isLoading={list.isPending || !views.ready || list.isPlaceholderData}
          isError={list.isError}
          onRetry={() => void list.refetch()}
          sort={sort}
          onSortChange={setSort}
          onOpen={(id) => navigate(`/products/${id}`)}
          onCreate={() =>
            workTab.managed ? navigate(newWorkPath('/products/new')) : setFormOpen(true)
          }
          hasFilters={hasFilters}
          canCreate={canWrite}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}

      {meta === undefined ? null : (
        <nav
          className="flex items-center justify-between gap-4"
          aria-label={t('common.pagination')}
        >
          <p className="text-xs text-muted-foreground">
            {t('common.paginationInfo', {
              page: meta.page,
              totalPages: Math.max(meta.totalPages, 1),
              total: meta.total,
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page <= 1}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
            >
              {t('common.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page >= meta.totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </nav>
      )}

      <ProductFormSheet
        open={formOpen}
        onOpenChange={setFormOpen}
        product={null}
        onSaved={onProductSaved}
      />

      <BulkPriceDialog
        productIds={selectedIds}
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onApplied={() => setSelectedIds([])}
      />
      <ProductCategoryManager
        open={categoriesOpen}
        onOpenChange={setCategoriesOpen}
        canWrite={canWrite}
      />
    </div>
  );
}
